/**
 * UserNotificationSettingsDialog — admin-managed per-user notification
 * category toggles (Jun 2026). Opens from the user setup dialog.
 * Each category defaults by role; toggles differing from the role
 * default are saved as per-user overrides.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, BellRing } from 'lucide-react';

import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserNotificationSettingsDialog = ({ open, onClose, userId, userName }) => {
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    setLoadFailed(false);
    axios.get(`${API}/users/${userId}/notification-settings`)
      .then((r) => {
        setRole(r.data?.role || '');
        setCategories(r.data?.categories || []);
      })
      .catch(() => { setLoadFailed(true); toast.error('Failed to load notification settings'); })
      .finally(() => setLoading(false));
  }, [open, userId]);

  const toggle = useCallback((key, enabled) => {
    setCategories((prev) => prev.map((c) => (c.key === key ? { ...c, enabled } : c)));
  }, []);

  const resetToDefaults = useCallback(() => {
    setCategories((prev) => prev.map((c) => ({ ...c, enabled: c.role_default })));
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const overrides = {};
      categories.forEach((c) => {
        if (c.enabled !== c.role_default) overrides[c.key] = c.enabled;
      });
      const r = await axios.put(`${API}/users/${userId}/notification-settings`, { overrides });
      setCategories(r.data?.categories || []);
      toast.success('Notification settings saved');
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  }, [categories, userId, onClose]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="user-notification-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-teal-600" />
            Notification Settings{userName ? ` — ${userName}` : ''}
          </DialogTitle>
          <DialogDescription>
            Each category defaults on or off from the {role ? <strong className="capitalize">{role}</strong> : 'user'} role.
            Toggles changed here override the role default for this user only.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="divide-y border rounded-md" data-testid="notification-categories-list">
            {categories.map((c) => (
              <div key={c.key} className="flex items-start justify-between gap-3 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-800">{c.label}</p>
                    {c.enabled !== c.role_default && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700" data-testid={`notif-cat-override-badge-${c.key}`}>
                        Override
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{c.description}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">Role default: {c.role_default ? 'On' : 'Off'}</p>
                </div>
                <Switch
                  checked={c.enabled}
                  onCheckedChange={(v) => toggle(c.key, v)}
                  data-testid={`notif-cat-toggle-${c.key}`}
                />
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button
            variant="ghost"
            onClick={resetToDefaults}
            disabled={loading || saving}
            className="mr-auto text-gray-600"
            data-testid="notif-settings-reset-btn"
          >
            Reset to role defaults
          </Button>
          <Button variant="outline" onClick={() => onClose?.()} disabled={saving} data-testid="notif-settings-cancel-btn">
            Cancel
          </Button>
          <Button onClick={save} disabled={loading || saving || loadFailed || categories.length === 0} className="bg-teal-600 hover:bg-teal-700" data-testid="notif-settings-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserNotificationSettingsDialog;
export { UserNotificationSettingsDialog };
